import type {ComponentAction, ConversionDal, StructureIterators} from '../../types'
import type {Pointer} from '@wix/document-services-types'
import type {StructureEntryItem} from '../types'
import {pointerUtils} from '@wix/document-manager-core'
import {ChildrenProperties} from '../../enums'

export const defaultChildrenProperties: ChildrenProperties[] = [ChildrenProperties.components]

export const createStructureIterators = (conversionDal: ConversionDal, rootPointer: Pointer): StructureIterators => {
    const getStructureById = (id: string) => conversionDal.get(pointerUtils.getPointerFromPointer(id, rootPointer))

    const getChildrenToIterate = (comp: StructureEntryItem, childrenProperties: ChildrenProperties[]): string[] =>
        childrenProperties.reduce(
            (children: string[], childrenProperty: ChildrenProperties) => [
                ...children,
                ...((comp as any)[childrenProperty] || [])
            ],
            []
        )

    function* flatStructureIterator(
        comp: any,
        compId: string,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ): Generator<{component: any; id: string}> {
        yield {component: comp, id: compId}

        const childrenToIterate = getChildrenToIterate(comp, childrenProperties)

        for (const childId of childrenToIterate) {
            yield* flatStructureIterator(getStructureById(childId), childId, childrenProperties)
        }
    }

    function* postOrderFlatStructureIterator(
        comp: any,
        compId: string,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ): Generator<{component: any; id: string}> {
        const childrenToIterate = getChildrenToIterate(comp, childrenProperties)

        for (const childId of childrenToIterate) {
            yield* postOrderFlatStructureIterator(getStructureById(childId), childId, childrenProperties)
        }

        yield {component: comp, id: compId}
    }

    const forEachComponent = (
        action: ComponentAction,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ) => {
        for (const {id} of flatStructureIterator(conversionDal.get(rootPointer), rootPointer.id, childrenProperties)) {
            action(pointerUtils.getPointerFromPointer(id, rootPointer))
        }
    }

    const forEachComponentAsync = async (
        action: ComponentAction,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ) => {
        for (const {id} of flatStructureIterator(conversionDal.get(rootPointer), rootPointer.id, childrenProperties)) {
            await action(pointerUtils.getPointerFromPointer(id, rootPointer))
        }
    }

    const forEachComponentReverse = (
        action: ComponentAction,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ) => {
        for (const {id} of Array.from(
            flatStructureIterator(conversionDal.get(rootPointer), rootPointer.id, childrenProperties)
        ).reverse()) {
            action(pointerUtils.getPointerFromPointer(id, rootPointer))
        }
    }

    const forEachComponentPostOrder = (
        action: ComponentAction,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ) => {
        for (const {id} of postOrderFlatStructureIterator(
            conversionDal.get(rootPointer),
            rootPointer.id,
            childrenProperties
        )) {
            action(pointerUtils.getPointerFromPointer(id, rootPointer))
        }
    }

    const forEachComponentPostOrderAsync = async (
        action: ComponentAction,
        childrenProperties: ChildrenProperties[] = defaultChildrenProperties
    ) => {
        for (const {id} of postOrderFlatStructureIterator(
            conversionDal.get(rootPointer),
            rootPointer.id,
            childrenProperties
        )) {
            await action(pointerUtils.getPointerFromPointer(id, rootPointer))
        }
    }

    return {
        getRootPointer: () => rootPointer,
        forEachComponent,
        forEachComponentAsync,
        forEachComponentReverse,
        forEachComponentPostOrder,
        forEachComponentPostOrderAsync
    }
}
